// GlobalModal.js

import debug from "debug";
const log = debug("app:globalModal");

/**
 * Class to manage a global modal for notifications and actions.
 * @category Components
 * @subcategory GlobalModal
 */
export class GlobalModal {
  /**
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.modalElement = this.createModalElement();
    this.currentConfig = null;
    document.body.appendChild(this.modalElement);
    this.init();
  }

  /**
   * Initializes the modal by setting up event listeners.
   */
  init() {
    log("🔔 GlobalModal initialized, listening for modalInfo events");
    this.eventHub.on("modalInfo", (data) => {
      log("📨 GlobalModal received modalInfo event:", data);
      this.showModal(data);
    });
  }


  cleanup() {
    console.log("GlobalModal cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("modalInfo", this.showModal);
    }
  
    // 2. Remove DOM event listener from close button
    const closeButton = this.modalElement?.querySelector(".global-modal-close-btn");
    if (closeButton) {
      closeButton.removeEventListener("click", this.hideModal);
    }
  
    // 3. Remove the modal from the DOM if it exists
    if (this.modalElement && this.modalElement.parentNode) {
      this.modalElement.parentNode.removeChild(this.modalElement);
    }
  
    // 4. Nullify object references
    this.eventHub = null;
    this.modalElement = null;
  
    console.log("GlobalModal cleanup completed.");
  }

  /**
   * Creates the modal element and sets up the structure.
   * @returns {HTMLElement} The modal wrapper element.
   */
  createModalElement() {
    const modalWrapper = document.createElement("div");
    modalWrapper.classList.add("global-modal-wrapper");
    modalWrapper.innerHTML = `
            <div class="global-modal">
                <div class="global-modal-banner" style="display: none;"></div>
                <div class="global-modal-header">
                    <span class="global-modal-title"></span>
                    <button class="global-modal-close-btn">&times;</button>
                </div>
                <div class="global-modal-body">
                    <div class="global-modal-message"></div>
                </div>
                <div class="global-modal-footer"></div>
            </div>
        `;

    modalWrapper
      .querySelector(".global-modal-close-btn")
      .addEventListener("click", () => {
        this.handleClose();
      });

    return modalWrapper;
  }

  /**
   * Displays the modal with the given data.
   * @param {Object} data - Data for configuring the modal.
   * @param {string} [data.title] - The title of the modal.
   * @param {string} [data.message] - The message to display in the modal body.
   * @param {Array} [data.actions] - An array of action objects for modal buttons.
   * @param {string} data.actions[].label - The label of the button.
   * @param {Function} [data.actions[].callback] - Callback function to execute on button click.
   * @param {boolean} [data.autoClose] - Whether the modal should automatically close.
   * @param {number} [data.timeout] - The timeout in milliseconds for auto-closing the modal.
   */
  showModal(data) {
    log("🎭 GlobalModal.showModal called with data:", data);
    const {
      title,
      message,
      messageHtml,
      actions,
      autoClose,
      timeout,
      variant,
      showDontShowAgainToggle,
      dontShowAgainLabel,
      headerImage,
    } = data;

    this.currentConfig = data || null;

    if (!this.modalElement) {
      log("❌ GlobalModal modalElement is null!");
      return;
    }

    const titleElement = this.modalElement.querySelector(".global-modal-title");
    const messageElement = this.modalElement.querySelector(".global-modal-message");
    const bodyElement = this.modalElement.querySelector(".global-modal-body");
    
    if (!titleElement || !messageElement) {
      log("❌ GlobalModal: Could not find title or message elements!");
      return;
    }

    // Optional header image (banner)
    const bannerElement = this.modalElement.querySelector(".global-modal-banner");
    if (bannerElement) {
      if (headerImage) {
        bannerElement.innerHTML = `<img src="${headerImage}" alt="" />`;
        bannerElement.style.display = "";
      } else {
        bannerElement.innerHTML = "";
        bannerElement.style.display = "none";
      }
    }

    titleElement.textContent = title || "Notification";
    if (messageHtml != null) {
      messageElement.innerHTML = messageHtml;
    } else {
      messageElement.textContent = message || "";
    }

    // Reset any existing toggle content
    if (bodyElement) {
      const existingToggle = bodyElement.querySelector(".global-modal-toggle");
      if (existingToggle) {
        existingToggle.remove();
      }
    }

    const footer = this.modalElement.querySelector(".global-modal-footer");
    footer.innerHTML = "";

    // Optional "Don't show again" toggle rendered below the message
    if (showDontShowAgainToggle && bodyElement) {
      const toggleWrapper = document.createElement("label");
      toggleWrapper.classList.add("global-modal-toggle");

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.classList.add("global-modal-toggle-checkbox");

      const labelSpan = document.createElement("span");
      labelSpan.textContent =
        dontShowAgainLabel || "Don't show this update again";

      toggleWrapper.appendChild(checkbox);
      toggleWrapper.appendChild(labelSpan);

      bodyElement.appendChild(toggleWrapper);
    }

    if (actions && actions.length > 0) {
      actions.forEach((action, index) => {
        const actionBtn = document.createElement("button");
        actionBtn.textContent = action.label;
        actionBtn.classList.add("modal-action-button");

        const labelLower = action.label.toLowerCase();

        if (labelLower === "cancel") {
          actionBtn.classList.add("cancel");
        } else if (labelLower === "continue") {
          actionBtn.classList.add("continue");
        }

        // Style as primary blue when explicitly set or for design-name-conflict
        if (action.primary || (variant === "design-name-conflict" && labelLower !== "cancel")) {
          actionBtn.classList.add("primary");
        }

        actionBtn.addEventListener("click", () => {
          if (typeof action.callback === "function") {
            action.callback();
          }
          this.hideModal();
        });

        footer.appendChild(actionBtn);
      });
    }

    log("✅ GlobalModal: Adding 'visible' class to modal wrapper");
    this.modalElement.classList.add("visible");
    log("✅ GlobalModal: Modal should now be visible. Element classes:", this.modalElement.className);

    // Variant-specific behaviors (e.g., highlight 3D toggle in header)
    if (variant === "3d-highlight") {
      const header3dToggle = document.getElementById("header-3d-toggle");
      if (header3dToggle) {
        header3dToggle.classList.add("header-3d-mode-highlight");
      }
    }

    if (autoClose && timeout) {
      setTimeout(() => this.hideModal(), timeout);
    }
  }

  /**
   * Handles close button click.
   */
  handleClose() {
    this.hideModal();
  }

  /**
   * Hides the modal and removes its visible state.
   */
  hideModal() {
    this.modalElement.classList.remove("visible");
    this.currentConfig = null;

    // Clear any variant-specific UI (e.g., 3D toggle highlight)
    const header3dToggle = document.getElementById("header-3d-toggle");
    if (header3dToggle) {
      header3dToggle.classList.remove("header-3d-mode-highlight");
    }
  }
}
